import React, { useState } from "react";
import {
	Card,
	CardContent,
	Typography,
	Box,
	Button,
	List,
	ListItem,
	ListItemText,
	ListItemSecondaryAction,
	IconButton,
	TextField,
	Grid,
	Avatar,
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
} from "@material-ui/core";
import {
	Plus as AddIcon,
	Trash2 as DeleteIcon,
	Edit as EditIcon,
	Circle as CircleIcon,
} from "react-feather";

interface StateItem {
	id: string;
	name: string;
	description: string;
	color: string;
}

const predefinedColors = [
	"#f44336", // red
	"#e91e63", // pink
	"#9c27b0", // purple
	"#673ab7", // deep purple
	"#3f51b5", // indigo
	"#2196f3", // blue
	"#03a9f4", // light blue
	"#00bcd4", // cyan
	"#009688", // teal
	"#4caf50", // green
	"#8bc34a", // light green
	"#cddc39", // lime
	"#ffeb3b", // yellow
	"#ffc107", // amber
	"#ff9800", // orange
	"#ff5722", // deep orange
];

export const NomenclatureTab = () => {
	const [states, setStates] = useState<StateItem[]>([]);
	const [dialogOpen, setDialogOpen] = useState(false);
	const [editingState, setEditingState] = useState<StateItem | null>(null);
	const [formData, setFormData] = useState({
		name: "",
		description: "",
		color: predefinedColors[0],
	});

	const openDialog = (state?: StateItem) => {
		if (state) {
			setEditingState(state);
			setFormData({
				name: state.name,
				description: state.description,
				color: state.color,
			});
		} else {
			setEditingState(null);
			setFormData({
				name: "",
				description: "",
				color: predefinedColors[0],
			});
		}
		setDialogOpen(true);
	};

	const closeDialog = () => {
		setDialogOpen(false);
		setEditingState(null);
		setFormData({
			name: "",
			description: "",
			color: predefinedColors[0],
		});
	};

	const saveState = () => {
		if (!formData.name.trim()) return;

		if (editingState) {
			// Edit existing state
			setStates(states.map(state =>
				state.id === editingState.id
					? { ...state, ...formData, name: formData.name.trim(), description: formData.description.trim() }
					: state
			));
		} else {
			// Add new state
			const newState: StateItem = {
				id: Date.now().toString(),
				name: formData.name.trim(),
				description: formData.description.trim(),
				color: formData.color,
			};
			setStates([...states, newState]);
		}
		closeDialog();
	};

	const deleteState = (id: string) => {
		setStates(states.filter(state => state.id !== id));
	};

	return (
		<Card>
			<CardContent>
				<Typography variant="h6" gutterBottom>
					Nomenclatura
				</Typography>
				
				<Box mb={2}>
					<Typography variant="subtitle1" gutterBottom>
						Estados
					</Typography>
					<Typography variant="body2" color="textSecondary" gutterBottom>
						Gestiona los estados con sus descripciones y colores para el reporte
					</Typography>
					
					<Button
						variant="contained"
						color="primary"
						startIcon={<AddIcon />}
						onClick={() => openDialog()}
						style={{ marginBottom: 16 }}
					>
						Agregar Estado
					</Button>
				</Box>

				<List>
					{states.map((state, index) => (
						<ListItem key={state.id} divider={index < states.length - 1}>
							<Avatar
								style={{
									backgroundColor: state.color,
									width: 24,
									height: 24,
									marginRight: 16,
								}}
							>
								<CircleIcon style={{ fontSize: 16 }} />
							</Avatar>
							<ListItemText
								primary={state.name}
								secondary={state.description}
								primaryTypographyProps={{ variant: 'body1' }}
								secondaryTypographyProps={{ variant: 'body2' }}
							/>
							<ListItemSecondaryAction>
								<IconButton
									edge="end"
									onClick={() => openDialog(state)}
									color="primary"
									size="small"
									style={{ marginRight: 8 }}
								>
									<EditIcon fontSize="small" />
								</IconButton>
								<IconButton
									edge="end"
									onClick={() => deleteState(state.id)}
									color="secondary"
									size="small"
								>
									<DeleteIcon fontSize="small" />
								</IconButton>
							</ListItemSecondaryAction>
						</ListItem>
					))}
				</List>

				{states.length === 0 && (
					<Typography variant="body2" color="textSecondary" style={{ textAlign: 'center', padding: 32 }}>
						No hay estados agregados
					</Typography>
				)}

				{/* Dialog for Add/Edit State */}
				<Dialog open={dialogOpen} onClose={closeDialog} maxWidth="sm" fullWidth>
					<DialogTitle>
						{editingState ? 'Editar Estado' : 'Agregar Estado'}
					</DialogTitle>
					<DialogContent>
						<Grid container spacing={2}>
							<Grid item xs={12}>
								<TextField
									fullWidth
									variant="outlined"
									label="Nombre del Estado"
									value={formData.name}
									onChange={(e) => setFormData({ ...formData, name: e.target.value })}
									margin="normal"
									required
								/>
							</Grid>
							<Grid item xs={12}>
								<TextField
									fullWidth
									variant="outlined"
									label="Descripción"
									value={formData.description}
									onChange={(e) => setFormData({ ...formData, description: e.target.value })}
									margin="normal"
									multiline
									rows={3}
								/>
							</Grid>
							<Grid item xs={12}>
								<Typography variant="subtitle2" gutterBottom style={{ marginTop: 16 }}>
									Color del Estado
								</Typography>
								<Grid container spacing={1}>
									{predefinedColors.map((color) => (
										<Grid item key={color}>
											<IconButton
												onClick={() => setFormData({ ...formData, color })}
												style={{
													backgroundColor: color,
													width: 32,
													height: 32,
													border: formData.color === color ? '3px solid #000' : '1px solid #ccc',
													margin: 2,
												}}
											>
												<CircleIcon style={{ fontSize: 16, color: 'white' }} />
											</IconButton>
										</Grid>
									))}
								</Grid>
							</Grid>
						</Grid>
					</DialogContent>
					<DialogActions>
						<Button onClick={closeDialog} color="secondary">
							Cancelar
						</Button>
						<Button 
							onClick={saveState} 
							color="primary" 
							variant="contained"
							disabled={!formData.name.trim()}
						>
							{editingState ? 'Actualizar' : 'Agregar'}
						</Button>
					</DialogActions>
				</Dialog>
			</CardContent>
		</Card>
	);
};